/** CMS Nursing Home Compare (PDC) facility-type gate — mirrors rules server cms_nursing_home_pdc.py */

export const CMS_NURSING_HOME_PDC_CANONICAL_SLUGS = [
  "skilled_nursing_facility",
  "nursing_facility_medicaid",
  "nursing_care_facility",
  "swing_bed_unit",
  "dual_certified_snf_nf",
] as const;

/** Fallback labels when API has not supplied applicable_type_labels. */
export const CMS_NURSING_HOME_PDC_FALLBACK_LABELS = [
  "Standalone SNF (Skilled Nursing Facility)",
  "Standalone NF (Nursing Facility)",
  "Nursing Care Facility",
  "Medicare Defined Swing Bed Unit",
  "Nursing Home (dually certified SNF + NF)",
] as const;

export interface CmsNursingHomePdcMeta {
  applies?: boolean;
  applicable_type_labels?: string[];
}

export type CmsNursingHomePdcFacilityLike = {
  covered_by_cms_nursing_home_pdc?: boolean;
  facility_type_slug?: string | null;
  facility_type?: string | null;
};

const CMS_NURSING_HOME_PDC_ALIAS_SLUGS: readonly string[] = [
  "snf",
  "nf",
  "nursing_home",
  "nursing_facility",
  "dual_snf_nf",
];

function normalizeSlug(raw: string): string {
  let normalized = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
  const paren = normalized.indexOf("_(");
  if (paren > 0) normalized = normalized.slice(0, paren);
  return normalized;
}

export function isCmsNursingHomePdcSlug(facilityTypeSlug: string): boolean {
  const s = normalizeSlug(facilityTypeSlug);
  if (!s) return false;
  if ((CMS_NURSING_HOME_PDC_CANONICAL_SLUGS as readonly string[]).includes(s)) {
    return true;
  }
  return CMS_NURSING_HOME_PDC_ALIAS_SLUGS.includes(s);
}

export function isCmsNursingHomePdcFacility(
  facility: CmsNursingHomePdcFacilityLike | null | undefined,
): boolean {
  if (!facility) return false;
  if (facility.covered_by_cms_nursing_home_pdc === true) return true;
  const slug =
    (facility.facility_type_slug ?? "").trim() ||
    normalizeSlug(facility.facility_type ?? "");
  return isCmsNursingHomePdcSlug(slug);
}

export function resolveCmsNursingHomePdcTypeLabels(
  meta?: CmsNursingHomePdcMeta | null,
): string[] {
  const fromApi = meta?.applicable_type_labels?.filter(Boolean) ?? [];
  if (fromApi.length > 0) return fromApi;
  return [...CMS_NURSING_HOME_PDC_FALLBACK_LABELS];
}

export function cmsNursingHomePdcTooltipAriaLabel(): string {
  return "CMS Nursing Home Compare facility type applicability";
}

export function cmsNursingHomePdcTooltipHeading(): string {
  return "Applies to CMS Nursing Home Compare (PDC) facility types only";
}

export function cmsNursingHomePdcTooltipIntro(): string {
  return "This section uses data and requirements scoped to these facility types:";
}
